import { put, takeEvery, takeLatest, call } from 'redux-saga/effects'
import { AxiosResponse } from 'axios'
import * as Api from 'services/api'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CompaniesSuggestedResponse, CompanyBenchmarkResponse, CompanySearchResponse, SuggestedCompany } from './types'

let suggestionRequestController = new AbortController()

type loadingTypes = {
  suggestions: boolean
  companySearch: boolean
}

type errorTypes = {
  suggestions: string | null
  search: string | null
  searchStatus: string | null
}

type ErrorKeyValueType = {
  key: keyof errorTypes
  value: null | string
}

type CompanyState = {
  suggestions: SuggestedCompany[]
  searchResponse: CompanySearchResponse | null
  benchmarkResponse: CompanyBenchmarkResponse | null
  loading: loadingTypes
  error: errorTypes
}

const initialState: CompanyState = {
  suggestions: [],
  searchResponse: null,
  benchmarkResponse: null,

  loading: {
    suggestions: false,
    companySearch: false,
  },
  error: {
    suggestions: null,
    search: null,
    searchStatus: null,
  },
}

/**
 * State initalisation and reducer definitions
 * If being watched as a saga, define as an empty function
 */
const slice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    getSuggestedCompanies: (state, action: PayloadAction<string>) => {},
    setSuggestedCompanies: (state, action: PayloadAction<CompaniesSuggestedResponse>) => {
      state.suggestions = action.payload.suggestedCompanies
    },
    submitCompanySearch: (state, action: PayloadAction<string[]>) => {},
    setSearchReponse: (state, action: PayloadAction<CompanySearchResponse>) => {
      state.searchResponse = action.payload
    },
    submitCompanySearchForBenchmark: (state, action: PayloadAction<string[]>) => {},
    setBenchmarkResponse: (state, action: PayloadAction<CompanyBenchmarkResponse>) => {
      state.benchmarkResponse = action.payload
    },
    getStatus: (state, action: PayloadAction<string | null>) => {},
    getCompanySearchForBenchmarkStatus: (state, action: PayloadAction<string | null>) => {},
    regenerateCompanySearch: (state, action: PayloadAction<string>) => {},
    setLoading: (state, action: PayloadAction<{ key: 'suggestions' | 'companySearch'; value: boolean }>) => {
      const { key, value } = action.payload
      state.loading[key] = value
    },
    setError: (state, action: PayloadAction<ErrorKeyValueType>) => {
      const { key, value } = action.payload
      state.error[key] = value
    },
  },
})

export const actions = slice.actions
export const reducer = slice.reducer

/**
 * Saga watcher
 */
export function* sagas() {
  yield takeEvery(actions.getSuggestedCompanies, getSuggestedCompaniesSaga)
  yield takeEvery(actions.submitCompanySearch, submitCompanySearchSaga)
  yield takeEvery(actions.submitCompanySearchForBenchmark, submitCompanySearchForBenchmarkSaga)
  yield takeLatest(actions.getStatus, getStatusSaga)
  yield takeLatest(actions.getCompanySearchForBenchmarkStatus, getCompanySearchForBenchmarkStatusSaga)
  yield takeLatest(actions.regenerateCompanySearch, regenerateCompanySearchSaga)
}

const GENERAL_ERROR = 'Something went wrong, please try again. \nIf the problem continues, please contact us.'
/**
 * Sagas
 */

export const getSuggestedCompaniesSaga = function* (action: PayloadAction<string>) {
  const startWith = action.payload
  yield put(actions.setLoading({ key: 'suggestions', value: true }))

  try {
    // yield call(suggestionRequestController.abort) // cancel any previous API calls
    suggestionRequestController.abort() // cancel any previous API calls

    suggestionRequestController = new AbortController()

    const response: AxiosResponse<CompaniesSuggestedResponse> = yield call(
      Api.authGet,
      `${Api.ENDPOINTS.companySuggestions}`,
      {
        starts_with: startWith,
      },
      suggestionRequestController.signal,
    )

    yield put(actions.setLoading({ key: 'suggestions', value: false }))
    yield put(actions.setSuggestedCompanies(response.data))
  } catch (error: any) {
    if (error.code !== 'ERR_CANCELED') {
      yield put(actions.setLoading({ key: 'suggestions', value: false }))
      yield put(actions.setError({ key: 'suggestions', value: GENERAL_ERROR }))
      console.log(error)
    }
  }
}

export const submitCompanySearchSaga = function* (action: PayloadAction<string[]>) {
  yield put(actions.setError({ key: 'search', value: null }))
  yield put(actions.setLoading({ key: 'companySearch', value: true }))
  try {
    const response: AxiosResponse<CompanySearchResponse> = yield call(Api.authPostForm, Api.ENDPOINTS.companySearch, {
      company_names: action.payload,
    })

    yield put(actions.setLoading({ key: 'companySearch', value: false }))

    if (response.data?.ID) {
      yield put(actions.setSearchReponse(response.data))
    } else {
      //TODO: if no company search returned???
    }
  } catch (error: any) {
    yield put(actions.setLoading({ key: 'companySearch', value: false }))
    yield put(actions.setError({ key: 'search', value: GENERAL_ERROR }))
    console.log(error)
  }
}

export const submitCompanySearchForBenchmarkSaga = function* (action: PayloadAction<string[]>) {
  yield put(actions.setError({ key: 'search', value: null }))
  yield put(actions.setLoading({ key: 'companySearch', value: true }))
  try {
    const response: AxiosResponse<CompanyBenchmarkResponse> = yield call(Api.authPostForm, Api.ENDPOINTS.companyBenchmark, {
      company_names: action.payload,
    })

    yield put(actions.setLoading({ key: 'companySearch', value: false }))

    yield put(actions.setBenchmarkResponse(response.data))
  } catch (error: any) {
    yield put(actions.setLoading({ key: 'companySearch', value: false }))
    yield put(actions.setError({ key: 'search', value: GENERAL_ERROR }))
    console.log(error)
  }
}

export const regenerateCompanySearchSaga = function* (action: PayloadAction<string>) {
  const searchId = action.payload
  yield put(actions.setError({ key: 'search', value: null }))
  yield put(actions.setLoading({ key: 'companySearch', value: true }))
  try {
    const response: AxiosResponse<CompanySearchResponse> = yield call(
      Api.authPost,
      Api.ENDPOINTS.companySearchRegenerate(searchId),
      {},
    )
    yield put(actions.setLoading({ key: 'companySearch', value: false }))

    yield put(actions.setSearchReponse(response.data))
  } catch (error: any) {
    yield put(actions.setLoading({ key: 'companySearch', value: false }))
    yield put(actions.setError({ key: 'search', value: GENERAL_ERROR }))
    console.log(error)
  }
}

const getStatusSaga = function* (action: any) {
  const searchId = action.payload
  try {
    const response: AxiosResponse<any> = yield call(Api.authPost, Api.ENDPOINTS.companySearchStatus(searchId), {})
    yield put(actions.setSearchReponse(response.data))
  } catch (error: any) {
    let message = GENERAL_ERROR
    if (error.response.status === 404) {
      message = 'Company search not found. Please verify the ID and try again.'
    }

    yield put(actions.setError({ key: 'searchStatus', value: message }))
    console.log(error)
  }
}

const getCompanySearchForBenchmarkStatusSaga = function* (action: any) {
  const searchId = action.payload
  try {
    const response: AxiosResponse<any> = yield call(Api.authPost, Api.ENDPOINTS.companySearchStatus(searchId), {})
    yield put(actions.setBenchmarkResponse(response.data))
  } catch (error: any) {
    let message = GENERAL_ERROR
    if (error.response.status === 404) {
      message = 'Company search not found. Please verify the ID and try again.'
    }

    yield put(actions.setError({ key: 'searchStatus', value: message }))
    console.log(error)
  }
}
